import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS, formatPrice } from '@/lib/index';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, Phone, MapPin, CreditCard, Calendar, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { useOrders } from '@/hooks/useOrders';

export default function AdminOrders() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getOrders, loading } = useOrders();
  const [orders, setOrders] = useState<any[]>([]);

  // التحقق من تسجيل الدخول
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      navigate(ROUTE_PATHS.ADMIN_LOGIN);
    }
  }, [navigate]);

  // جلب الطلبات
  useEffect(() => {
    const fetchOrders = async () => {
      const result = await getOrders();
      if (result.success && result.data) {
        setOrders(result.data);
      }
    };
    fetchOrders();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminUsername');
    toast({
      title: 'تم تسجيل الخروج',
      description: 'تم تسجيل خروجك بنجاح',
    });
    navigate(ROUTE_PATHS.ADMIN_LOGIN);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      pending: 'secondary',
      processing: 'default',
      shipped: 'default',
      delivered: 'outline',
      cancelled: 'destructive',
    };

    const labels: Record<string, string> = {
      pending: 'قيد الانتظار',
      processing: 'قيد المعالجة',
      shipped: 'تم الشحن',
      delivered: 'تم التوصيل',
      cancelled: 'ملغي',
    };

    return (
      <Badge variant={variants[status] || 'default'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      cash: 'كاش عند الاستلام',
      vodafone: 'فودافون كاش',
      instapay: 'إنستاباي',
    };
    return labels[method] || method;
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          dir="rtl"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2">إدارة الطلبات</h1>
              <p className="text-muted-foreground">عرض وإدارة جميع الطلبات</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/admin/products')}>
                <Package className="ml-2 h-4 w-4" />
                المنتجات
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="ml-2 h-4 w-4" />
                تسجيل الخروج
              </Button>
            </div>
          </div>

          {/* Orders Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse">
                <div className="h-8 bg-muted rounded w-48 mx-auto mb-4"></div>
                <div className="h-4 bg-muted rounded w-64 mx-auto"></div>
              </div>
            </div>
          ) : orders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">لا توجد طلبات</h3>
                <p className="text-muted-foreground">لم يتم تقديم أي طلبات بعد</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">
                          طلب #{order.id.slice(0, 8)}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(order.created_at).toLocaleDateString('ar-EG', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </CardDescription>
                      </div>
                      <div className="text-left">
                        {getStatusBadge(order.order_status)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* معلومات العميل */}
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm text-muted-foreground">
                          معلومات العميل
                        </h4>
                        <div className="space-y-1">
                          <p className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-muted-foreground" />
                            {order.customer_name}
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            {order.customer_phone}
                          </p>
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            {order.shipping_city} - {order.shipping_address}
                          </p>
                          {order.shipping_notes && (
                            <p className="text-sm text-muted-foreground">
                              ملاحظات: {order.shipping_notes}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* معلومات الدفع */}
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm text-muted-foreground">
                          معلومات الدفع
                        </h4>
                        <div className="space-y-1">
                          <p className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-muted-foreground" />
                            {getPaymentMethodLabel(order.payment_method)}
                          </p>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">المجموع الفرعي:</span>
                              <span>{formatPrice(order.total_amount - order.shipping_fee)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">الشحن:</span>
                              <span>{formatPrice(order.shipping_fee)}</span>
                            </div>
                            <div className="flex justify-between font-semibold text-base pt-2 border-t">
                              <span>الإجمالي:</span>
                              <span className="text-primary">{formatPrice(order.total_amount)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* المنتجات */}
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm text-muted-foreground">
                        المنتجات ({order.items?.length || 0})
                      </h4>
                      <div className="space-y-2">
                        {order.items?.map((item: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg"
                          >
                            <img
                              src={item.product_image}
                              alt={item.product_name}
                              className="w-12 h-12 object-cover rounded"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{item.product_name}</p>
                              <p className="text-xs text-muted-foreground">
                                الكمية: {item.quantity} × {formatPrice(item.price)}
                              </p>
                            </div>
                            <p className="font-semibold">{formatPrice(item.total)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* إحصائيات */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
            <Card>
              <CardContent className="p-6">
                <div className="text-sm text-muted-foreground mb-1">إجمالي الطلبات</div>
                <div className="text-3xl font-bold">{orders.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-sm text-muted-foreground mb-1">قيد الانتظار</div>
                <div className="text-3xl font-bold text-amber-500">
                  {orders.filter((o) => o.order_status === 'pending').length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-sm text-muted-foreground mb-1">تم التوصيل</div>
                <div className="text-3xl font-bold text-green-500">
                  {orders.filter((o) => o.order_status === 'delivered').length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-sm text-muted-foreground mb-1">إجمالي المبيعات</div>
                <div className="text-3xl font-bold text-primary">
                  {formatPrice(orders.reduce((sum, o) => sum + o.total_amount, 0))}
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
